import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AgoraRTC, { useRemoteUsers, useRTCClient } from 'agora-rtc-react';
import { toast } from 'react-hot-toast';
import { getCallToken } from '../../store/appointment/appointmentActions';
import { resetCallStats, setCallStats } from './callStatsStore';
import { getCallTiming, resetCallTiming, setCallTiming } from './callTimingStore';

/** Agora RemoteStreamType: HIGH = 0, LOW = 1 */
const STREAM_HIGH = 0;
const STREAM_LOW = 1;

export const CallMediaContext = createContext(null);

export const useCallMedia = () => useContext(CallMediaContext);

const QUALITY_LABELS = {
  0: 'unknown',
  1: 'excellent',
  2: 'good',
  3: 'poor',
  4: 'bad',
  5: 'very-bad',
  6: 'down',
};

const isPermissionCancel = (err) => {
  const code = err?.code || err?.name || '';
  const msg = String(err?.message || '').toLowerCase();
  return (
    code === 'PERMISSION_DENIED' ||
    code === 'NotAllowedError' ||
    msg.includes('permission denied') ||
    msg.includes('cancel') ||
    msg.includes('dismissed')
  );
};

/**
 * Headless media owner — joins once, owns tracks for the session life.
 * Must render under AgoraRTCProvider while a call session is active.
 */
export const CallMediaEngine = ({
  session,
  updateSessionToken,
  onSessionEnd,
  onAutoEndNavigate,
  teardownRef,
  children,
}) => {
  const client = useRTCClient();
  const hookUsers = useRemoteUsers();

  const [micOn, setMicOnState] = useState(true);
  const [camOn, setCamOnState] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionState, setConnectionState] = useState('DISCONNECTED');
  const [error, setError] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [micAvailable, setMicAvailable] = useState(true);
  const [camAvailable, setCamAvailable] = useState(true);
  const [screenShareSupported] = useState(
    () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia),
  );
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [joining, setJoining] = useState(true);
  /** Stable remote presence — updated only from join/left/publish events */
  const [remoteUser, setRemoteUser] = useState(null);
  const [remotePresent, setRemotePresent] = useState(false);
  /** Bumps when remote tracks attach so UI re-reads mutated Agora user objects */
  const [remoteTick, setRemoteTick] = useState(0);

  const micTrackRef = useRef(null);
  const camTrackRef = useRef(null);
  const screenTrackRef = useRef(null);
  const screenAudioRef = useRef(null);
  const camOnBeforeShareRef = useRef(true);
  const camToggleLockRef = useRef(false);
  const micToggleLockRef = useRef(false);
  const joinedRef = useRef(false);
  const joiningRef = useRef(false);
  const hasJoinedOnceRef = useRef(false);
  const rejoiningRef = useRef(false);
  const lastRenewAtRef = useRef(0);
  const endingRef = useRef(false);
  const warnShownRef = useRef(false);
  const courseStartedToastRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const hardDisconnectTimerRef = useRef(null);
  const hardReconnectBackoffRef = useRef(null);
  const waitingAloneTimerRef = useRef(null);
  const poorUplinkCountRef = useRef(0);
  const poorToastShownRef = useRef(false);
  const playedAudioIdsRef = useRef(new Set());
  const remoteUidKeyRef = useRef('');
  const remoteMediaKeyRef = useRef('');
  const lobbyAudioPausedRef = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const clearPlayedAudioCache = useCallback(() => {
    playedAudioIdsRef.current.clear();
  }, []);

  const playRemoteAudioTrack = useCallback((user) => {
    const track = user?.audioTrack;
    if (!track) return;
    // Wait lobby: subscribe but do not play remote audio until course starts
    if (!getCallTiming().sessionStarted) {
      lobbyAudioPausedRef.current = true;
      try {
        if (track.isPlaying) track.stop();
      } catch (_) {}
      return;
    }
    const id = track.getTrackId?.() || `${user.uid}-audio`;
    try {
      if (track.isPlaying) {
        playedAudioIdsRef.current.add(id);
        setAudioBlocked(false);
        return;
      }
    } catch (_) {}
    // Always re-play when not actively playing (stale id cache must not block)
    track
      .play()
      .then(() => {
        playedAudioIdsRef.current.add(id);
        setAudioBlocked(false);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        playedAudioIdsRef.current.delete(id);
        setAudioBlocked(true);
      });
  }, []);

  const resumeRemoteAudio = useCallback(() => {
    if (!client) return;
    lobbyAudioPausedRef.current = false;
    const users = client.remoteUsers || [];
    users.forEach((user) => {
      if (!user?.audioTrack) return;
      const id = user.audioTrack.getTrackId?.() || `${user.uid}-audio`;
      playedAudioIdsRef.current.delete(id);
      playRemoteAudioTrack(user);
    });
  }, [client, playRemoteAudioTrack]);

  const pauseLobbyRemoteAudio = useCallback(() => {
    if (!client) return;
    lobbyAudioPausedRef.current = true;
    (client.remoteUsers || []).forEach((user) => {
      const track = user?.audioTrack;
      if (!track) return;
      try {
        if (track.isPlaying) track.stop();
      } catch (_) {}
      const id = track.getTrackId?.() || `${user.uid}-audio`;
      playedAudioIdsRef.current.delete(id);
    });
  }, [client]);

  const clearHardDisconnectTimer = useCallback(() => {
    if (hardDisconnectTimerRef.current) {
      clearTimeout(hardDisconnectTimerRef.current);
      hardDisconnectTimerRef.current = null;
    }
  }, []);

  const bumpRemoteTickIfChanged = useCallback((user) => {
    const key = user
      ? `${user.uid}:${user.hasVideo ? 1 : 0}:${user.hasAudio ? 1 : 0}`
      : '';
    if (key === remoteMediaKeyRef.current) return;
    remoteMediaKeyRef.current = key;
    setRemoteTick((t) => t + 1);
  }, []);

  const serverNowMs = useCallback(
    () => Date.now() + (sessionRef.current?.serverOffsetMs || 0),
    [],
  );

  const isJoinWindowNearEnd = useCallback(() => {
    const closeAt = sessionRef.current?.joinCloseAt;
    if (!closeAt) return false;
    const closeMs = new Date(closeAt).getTime();
    if (Number.isNaN(closeMs)) return false;
    return closeMs - serverNowMs() <= 60_000;
  }, [serverNowMs]);

  const closeTrack = (track) => {
    if (!track) return;
    try {
      track.stop();
    } catch (_) {}
    try {
      track.close();
    } catch (_) {}
  };

  const cleanupTracks = useCallback(async () => {
    const published = [];
    if (micTrackRef.current) published.push(micTrackRef.current);
    if (camTrackRef.current) published.push(camTrackRef.current);
    if (screenTrackRef.current) published.push(screenTrackRef.current);
    if (screenAudioRef.current) published.push(screenAudioRef.current);
    if (published.length && client) {
      try {
        await client.unpublish(published);
      } catch (_) {}
    }
    closeTrack(micTrackRef.current);
    closeTrack(camTrackRef.current);
    closeTrack(screenTrackRef.current);
    closeTrack(screenAudioRef.current);
    micTrackRef.current = null;
    camTrackRef.current = null;
    screenTrackRef.current = null;
    screenAudioRef.current = null;
    setLocalVideoTrack(null);
    setLocalAudioTrack(null);
  }, [client]);

  const createAndPublishTracks = useCallback(async () => {
    let mic = null;
    let cam = null;

    try {
      mic = await AgoraRTC.createMicrophoneAudioTrack();
      micTrackRef.current = mic;
      setLocalAudioTrack(mic);
      setMicAvailable(true);
    } catch (err) {
      setMicAvailable(false);
      setMicOnState(false);
      const name = err?.name || err?.code || '';
      if (name === 'NotFoundError') {
        toast.error('No microphone found. Joining without mic.');
      } else if (name === 'NotAllowedError' || name === 'PERMISSION_DENIED') {
        toast.error('Microphone permission denied. Joining without mic.');
      } else {
        toast.error('Could not access microphone. Joining without mic.');
      }
    }

    try {
      cam = await AgoraRTC.createCameraVideoTrack();
      camTrackRef.current = cam;
      setLocalVideoTrack(cam);
      setCamAvailable(true);
    } catch (err) {
      setCamAvailable(false);
      setCamOnState(false);
      const name = err?.name || err?.code || '';
      if (name === 'NotFoundError') {
        toast.error('No camera found. Joining without camera.');
      } else if (name === 'NotAllowedError' || name === 'PERMISSION_DENIED') {
        toast.error('Camera permission denied. Joining without camera.');
      } else if (name === 'NotReadableError') {
        toast.error('Camera is in use by another app. Joining without camera.');
      } else {
        toast.error('Could not access camera. Joining without camera.');
      }
    }

    const toPublish = [mic, cam].filter(Boolean);
    if (toPublish.length) {
      await client.publish(toPublish);
    }
  }, [client]);

  const joinChannel = useCallback(async ({ force = false } = {}) => {
    const s = sessionRef.current;
    if (!client || !s) return;
    if (endingRef.current) return;
    if (joiningRef.current) return;
    const state = client.connectionState;
    if (!force && (state === 'CONNECTING' || state === 'CONNECTED' || state === 'RECONNECTING')) {
      joinedRef.current = state === 'CONNECTED' || state === 'RECONNECTING';
      if (joinedRef.current) hasJoinedOnceRef.current = true;
      if (state === 'CONNECTED') setConnectionState('CONNECTED');
      setJoining(false);
      console.debug('[call] join skipped — already', state);
      return;
    }

    joiningRef.current = true;
    setJoining(true);
    setError(null);
    clearHardDisconnectTimer();
    console.debug('[call] join start', { channel: s.channel, uid: s.uid, force });

    try {
      await client.join(s.appId, s.channel, s.token || null, s.uid);
      joinedRef.current = true;
      hasJoinedOnceRef.current = true;
      reconnectAttemptRef.current = 0;
      console.debug('[call] join success');
      try {
        await client.enableDualStream();
      } catch (_) {}
      if (!micTrackRef.current && !camTrackRef.current) {
        await createAndPublishTracks();
      } else {
        const tracks = [micTrackRef.current, camTrackRef.current].filter(Boolean);
        if (tracks.length) {
          try {
            await client.publish(tracks);
          } catch (_) {}
        }
      }
      // Peer may already be in-channel — subscribe immediately
      for (const user of client.remoteUsers || []) {
        try {
          if (user.hasAudio) await client.subscribe(user, 'audio');
          if (user.hasVideo) await client.subscribe(user, 'video');
        } catch (_) {}
      }
      if ((client.remoteUsers || []).length > 0) {
        setRemoteUser(client.remoteUsers[0]);
        setRemotePresent(true);
        bumpRemoteTickIfChanged(client.remoteUsers[0]);
      }
      // SDK may populate remoteUsers slightly after join resolves — second sweep
      setTimeout(() => {
        if (endingRef.current) return;
        const users = client.remoteUsers || [];
        if (users.length > 0) {
          setRemoteUser(users[0]);
          setRemotePresent(true);
          bumpRemoteTickIfChanged(users[0]);
          users.forEach((user) => {
            if (user.hasAudio) client.subscribe(user, 'audio').catch(() => {});
            if (user.hasVideo) client.subscribe(user, 'video').catch(() => {});
          });
        }
      }, 300);
      setConnectionState('CONNECTED');
    } catch (err) {
      console.debug('[call] join failed', err?.message || err);
      console.error('Agora join failed', err);
      setError(err?.message || 'Failed to join the call');
      setConnectionState('DISCONNECTED');
      throw err;
    } finally {
      joiningRef.current = false;
      setJoining(false);
    }
  }, [client, createAndPublishTracks, clearHardDisconnectTimer, bumpRemoteTickIfChanged]);

  const refetchToken = useCallback(async () => {
    const appointmentId = sessionRef.current?.appointmentId;
    if (!appointmentId) throw new Error('Missing appointment id for token refresh');
    const { default: store } = await import('../../store');
    const result = await store.dispatch(getCallToken(appointmentId));
    if (getCallToken.rejected.match(result)) {
      throw new Error(result.payload?.error || 'Token refresh failed');
    }
    const data = result.payload;
    updateSessionToken?.({
      token: data.token,
      joinCloseAt: data.joinCloseAt,
      joinOpenAt: data.joinOpenAt,
      uid: data.uid,
      channel: data.channel,
      serverNow: data.serverNow,
    });
    return data.token;
  }, [updateSessionToken]);

  const renewOrRejoin = useCallback(async ({ rejoin = false, trigger = 'unknown' } = {}) => {
    // Soft renew only — hard rejoin uses rejoiningRef lock instead
    if (!rejoin && Date.now() - lastRenewAtRef.current < 10_000) {
      console.debug('[call] renewOrRejoin throttled', { trigger, rejoin });
      return true;
    }
    if (rejoiningRef.current || joiningRef.current) {
      console.debug('[call] renewOrRejoin skipped — already in flight', { trigger, rejoin });
      return false;
    }

    clearHardDisconnectTimer();
    console.debug('[call] renewOrRejoin', { trigger, rejoin });

    if (rejoin) rejoiningRef.current = true;
    else lastRenewAtRef.current = Date.now();
    try {
      const token = await refetchToken();
      if (rejoin) {
        clearPlayedAudioCache();
        try {
          await client.leave();
        } catch (_) {}
        joinedRef.current = false;
        await joinChannel({ force: true });
      } else if (client && typeof client.renewToken === 'function') {
        await client.renewToken(token);
      }
      return true;
    } catch (err) {
      console.debug('[call] renewOrRejoin failed', err?.message || err);
      console.error('Token renew/rejoin failed', err);
      return false;
    } finally {
      if (rejoin) rejoiningRef.current = false;
    }
  }, [client, joinChannel, refetchToken, clearHardDisconnectTimer, clearPlayedAudioCache]);

  const attemptHardReconnect = useCallback(async () => {
    if (endingRef.current) return;
    if (rejoiningRef.current || joiningRef.current) {
      console.debug('[call] hard-reconnect bail — join/rejoin in flight');
      return;
    }
    // Never fight SDK while it is already reconnecting
    if (client?.connectionState === 'RECONNECTING' || client?.connectionState === 'CONNECTING') {
      return;
    }
    const attempt = reconnectAttemptRef.current;
    if (attempt >= 3) {
      setError('Connection lost. Tap Retry to rejoin.');
      return;
    }
    reconnectAttemptRef.current = attempt + 1;
    console.debug('[call] hard-reconnect attempt', reconnectAttemptRef.current);
    const ok = await renewOrRejoin({ rejoin: true, trigger: 'hard-reconnect' });
    if (!ok && reconnectAttemptRef.current < 3) {
      const delay = [2000, 4000, 8000][reconnectAttemptRef.current - 1] || 8000;
      if (hardReconnectBackoffRef.current) clearTimeout(hardReconnectBackoffRef.current);
      hardReconnectBackoffRef.current = setTimeout(() => {
        hardReconnectBackoffRef.current = null;
        attemptHardReconnect();
      }, delay);
    } else if (!ok) {
      setError('Connection lost. Tap Retry to rejoin.');
    }
  }, [client, renewOrRejoin]);

  // Initial join
  useEffect(() => {
    if (!session?.channel || !client) return undefined;
    endingRef.current = false;
    let cancelled = false;

    (async () => {
      try {
        await joinChannel();
      } catch (_) {
        if (cancelled || endingRef.current) return;
        const ok = await renewOrRejoin({ rejoin: true });
        if (!ok && !cancelled) {
          setError('Unable to join the call. Tap Retry.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.channel, session?.uid, client]);

  // Connection + token events — do not leave/rejoin on transient DISCONNECTED
  useEffect(() => {
    if (!client) return undefined;

    const endCallGracefully = async (message) => {
      if (endingRef.current) return;
      endingRef.current = true;
      clearHardDisconnectTimer();
      if (hardReconnectBackoffRef.current) {
        clearTimeout(hardReconnectBackoffRef.current);
        hardReconnectBackoffRef.current = null;
      }
      toast.error(message);
      await cleanupTracks();
      try {
        await client.leave();
      } catch (_) {}
      resetCallTiming();
      onSessionEnd?.();
      onAutoEndNavigate?.();
    };

    const onConnection = (cur, prev, reason) => {
      console.debug('[call] connection-state-change', { cur, prev, reason });
      setConnectionState(cur);
      if (cur === 'CONNECTED' || cur === 'RECONNECTING') {
        setError(null);
        if (cur === 'CONNECTED') {
          hasJoinedOnceRef.current = true;
          reconnectAttemptRef.current = 0;
        }
        clearHardDisconnectTimer();
        return;
      }
      if (cur === 'DISCONNECTED' && !endingRef.current) {
        // Intentional leave / in-flight rejoin must not schedule hard reconnect
        if (rejoiningRef.current || joiningRef.current) {
          console.debug('[call] ignore DISCONNECTED during join/rejoin');
          return;
        }
        const userLeave =
          reason === 'LEAVE' ||
          String(reason || '').toLowerCase().includes('leave');
        if (userLeave) return;
        // Wait — SDK often recovers via RECONNECTING without a hard rejoin
        clearHardDisconnectTimer();
        hardDisconnectTimerRef.current = setTimeout(() => {
          if (endingRef.current) return;
          if (rejoiningRef.current || joiningRef.current) return;
          if (client.connectionState === 'CONNECTED' || client.connectionState === 'RECONNECTING') {
            return;
          }
          attemptHardReconnect();
        }, 4000);
      }
    };

    const onTokenWillExpire = () => {
      console.debug('[call] token-privilege-will-expire');
      if (isJoinWindowNearEnd()) {
        console.debug('[call] skip renew — join window near end');
        return;
      }
      renewOrRejoin({ rejoin: false, trigger: 'token-will-expire' });
    };

    const onTokenDidExpire = async () => {
      console.debug('[call] token-privilege-did-expire');
      // Near joinCloseAt: rejoining with a tiny TTL loops forever — end gracefully
      if (isJoinWindowNearEnd()) {
        await endCallGracefully('Course time ended. Call closed.');
        return;
      }
      const ok = await renewOrRejoin({ rejoin: true, trigger: 'token-did-expire' });
      if (!ok) {
        await endCallGracefully('Call session expired.');
      }
    };

    const onNetworkQuality = (stats) => {
      const uplink = stats?.uplinkNetworkQuality ?? 0;
      const downlink = stats?.downlinkNetworkQuality ?? 0;
      setCallStats({ uplink, downlink });
      if (uplink >= 5) {
        poorUplinkCountRef.current += 1;
        if (poorUplinkCountRef.current >= 3 && !poorToastShownRef.current) {
          poorToastShownRef.current = true;
          toast('Your connection is unstable. Turning off the camera may help.');
        }
      } else {
        poorUplinkCountRef.current = 0;
      }
    };

    client.on('connection-state-change', onConnection);
    client.on('token-privilege-will-expire', onTokenWillExpire);
    client.on('token-privilege-did-expire', onTokenDidExpire);
    client.on('network-quality', onNetworkQuality);

    return () => {
      clearHardDisconnectTimer();
      if (hardReconnectBackoffRef.current) {
        clearTimeout(hardReconnectBackoffRef.current);
        hardReconnectBackoffRef.current = null;
      }
      client.off('connection-state-change', onConnection);
      client.off('token-privilege-will-expire', onTokenWillExpire);
      client.off('token-privilege-did-expire', onTokenDidExpire);
      client.off('network-quality', onNetworkQuality);
    };
  }, [
    client,
    attemptHardReconnect,
    renewOrRejoin,
    cleanupTracks,
    onSessionEnd,
    onAutoEndNavigate,
    clearHardDisconnectTimer,
    isJoinWindowNearEnd,
  ]);

  // RTC stats sampling
  useEffect(() => {
    if (!client) return undefined;
    let timer = null;

    const sample = () => {
      if (document.hidden) return;
      try {
        const rtc = client.getRTCStats?.() || {};
        const localVideo = client.getLocalVideoStats?.() || {};
        const firstKey = Object.keys(localVideo)[0];
        const v = firstKey ? localVideo[firstKey] : localVideo;
        setCallStats({
          bitrate: v?.sendBitrate ?? rtc?.OutgoingAvailableBandwidth ?? null,
          packetLoss: rtc?.RecvPacketLossRate ?? rtc?.SendPacketLossRate ?? null,
          rtt: rtc?.RTT ?? null,
          resolution:
            v?.sendResolutionWidth && v?.sendResolutionHeight
              ? `${v.sendResolutionWidth}x${v.sendResolutionHeight}`
              : null,
        });
      } catch (_) {}
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(sample, 3000);
      sample();
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVis = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      resetCallStats();
    };
  }, [client]);

  // Remote presence + subscribe — register once per client (no remoteUsers deps)
  useEffect(() => {
    if (!client) return undefined;

    const syncRemotePresence = () => {
      const users = client.remoteUsers || [];
      const primary = users[0] || null;
      setRemoteUser(primary);
      const uidKey = users.map((u) => String(u.uid)).sort().join(',');
      if (uidKey !== remoteUidKeyRef.current) {
        remoteUidKeyRef.current = uidKey;
        const type = sessionRef.current?.isMinimized ? STREAM_LOW : STREAM_HIGH;
        users.forEach((u) => {
          client.setRemoteVideoStreamType(u.uid, type).catch(() => {});
        });
      }
      if (users.length > 0) {
        if (waitingAloneTimerRef.current) {
          clearTimeout(waitingAloneTimerRef.current);
          waitingAloneTimerRef.current = null;
        }
        setRemotePresent(true);
      } else {
        // Debounce Waiting UI so brief empties don't flash
        if (waitingAloneTimerRef.current) clearTimeout(waitingAloneTimerRef.current);
        waitingAloneTimerRef.current = setTimeout(() => {
          if ((client.remoteUsers || []).length === 0) {
            setRemotePresent(false);
            setRemoteUser(null);
            bumpRemoteTickIfChanged(null);
          }
        }, 1000);
      }
      bumpRemoteTickIfChanged(primary);
    };

    const onPublished = async (user, mediaType) => {
      try {
        await client.subscribe(user, mediaType);
      } catch (_) {}
      if (mediaType === 'audio') playRemoteAudioTrack(user);
      syncRemotePresence();
    };

    const onUnpublished = (user, mediaType) => {
      if (mediaType === 'audio') {
        const id = user?.audioTrack?.getTrackId?.() || `${user?.uid}-audio`;
        playedAudioIdsRef.current.delete(id);
      }
      syncRemotePresence();
    };

    const onJoined = () => {
      syncRemotePresence();
    };

    const onLeft = (user) => {
      const id = `${user?.uid}-audio`;
      playedAudioIdsRef.current.delete(id);
      // Also drop any track-id keys for this uid
      [...playedAudioIdsRef.current].forEach((key) => {
        if (String(key).includes(String(user?.uid))) {
          playedAudioIdsRef.current.delete(key);
        }
      });
      syncRemotePresence();
    };

    client.on('user-published', onPublished);
    client.on('user-unpublished', onUnpublished);
    client.on('user-joined', onJoined);
    client.on('user-left', onLeft);

    // Catch-up after join / remount
    (client.remoteUsers || []).forEach((user) => {
      if (user.hasAudio) {
        client.subscribe(user, 'audio').then(() => playRemoteAudioTrack(user)).catch(() => {});
      }
      if (user.hasVideo) {
        client.subscribe(user, 'video').catch(() => {});
      }
    });
    syncRemotePresence();

    return () => {
      if (waitingAloneTimerRef.current) {
        clearTimeout(waitingAloneTimerRef.current);
        waitingAloneTimerRef.current = null;
      }
      client.off('user-published', onPublished);
      client.off('user-unpublished', onUnpublished);
      client.off('user-joined', onJoined);
      client.off('user-left', onLeft);
    };
  }, [client, bumpRemoteTickIfChanged, playRemoteAudioTrack]);

  // Dual-stream when minimize flag changes (uid set handled in syncRemotePresence)
  useEffect(() => {
    if (!client) return;
    const type = session?.isMinimized ? STREAM_LOW : STREAM_HIGH;
    (client.remoteUsers || []).forEach((user) => {
      client.setRemoteVideoStreamType(user.uid, type).catch(() => {});
    });
  }, [client, session?.isMinimized]);

  // Safety poll — only while connected and still waiting for peer
  useEffect(() => {
    if (!client) return undefined;
    if (connectionState !== 'CONNECTED') return undefined;
    if (remotePresent) return undefined;
    const id = setInterval(() => {
      const users = client.remoteUsers || [];
      if (users.length > 0) {
        setRemoteUser((prev) => prev || users[0]);
        setRemotePresent(true);
        bumpRemoteTickIfChanged(users[0]);
      }
    }, 700);
    return () => clearInterval(id);
  }, [client, connectionState, remotePresent, bumpRemoteTickIfChanged]);

  // Device change
  useEffect(() => {
    const onCam = async (info) => {
      if (info.state === 'ACTIVE' && camTrackRef.current && !isScreenSharing) {
        try {
          await camTrackRef.current.setDevice(info.device.deviceId);
          setCamAvailable(true);
        } catch (_) {}
      } else if (info.state === 'INACTIVE') {
        const devices = await AgoraRTC.getCameras().catch(() => []);
        if (!devices.length) {
          setCamAvailable(false);
          setCamOnState(false);
          toast.error('Camera disconnected.');
        }
      }
    };
    const onMic = async (info) => {
      if (info.state === 'ACTIVE' && micTrackRef.current) {
        try {
          await micTrackRef.current.setDevice(info.device.deviceId);
          setMicAvailable(true);
        } catch (_) {}
      } else if (info.state === 'INACTIVE') {
        const devices = await AgoraRTC.getMicrophones().catch(() => []);
        if (!devices.length) {
          setMicAvailable(false);
          setMicOnState(false);
          toast.error('Microphone disconnected.');
        }
      }
    };
    AgoraRTC.onCameraChanged = onCam;
    AgoraRTC.onMicrophoneChanged = onMic;
    return () => {
      AgoraRTC.onCameraChanged = undefined;
      AgoraRTC.onMicrophoneChanged = undefined;
    };
  }, [isScreenSharing]);

  // Countdown ticks → external store (does not thrash CallMediaContext)
  useEffect(() => {
    if (!session?.joinCloseAt) return undefined;
    const closeAt = new Date(session.joinCloseAt).getTime();
    if (Number.isNaN(closeAt)) return undefined;

    let startAt = null;
    if (session.joinOpenAt) {
      const openAt = new Date(session.joinOpenAt).getTime();
      if (!Number.isNaN(openAt)) startAt = openAt + 5 * 60 * 1000;
    } else {
      // joinCloseAt = start + 50m
      startAt = closeAt - 50 * 60 * 1000;
    }

    const tick = async () => {
      const now = Date.now() + (sessionRef.current?.serverOffsetMs || 0);
      const closeSec = Math.max(0, Math.ceil((closeAt - now) / 1000));
      const startSec = startAt != null ? Math.max(0, Math.ceil((startAt - now) / 1000)) : 0;
      const sessionStarted = startAt == null || now >= startAt;

      const wasStarted = getCallTiming().sessionStarted;
      setCallTiming({
        closeInSeconds: closeSec,
        startInSeconds: startSec,
        sessionStarted,
      });

      // Lobby → live: unlock remote audio playback
      if (sessionStarted && (lobbyAudioPausedRef.current || !wasStarted)) {
        resumeRemoteAudio();
      } else if (!sessionStarted && !lobbyAudioPausedRef.current) {
        pauseLobbyRemoteAudio();
      }

      if (sessionStarted && !courseStartedToastRef.current && startAt != null && startSec === 0) {
        // Fire once when crossing into session start
        const justStarted = now - startAt < 2000;
        if (justStarted) {
          courseStartedToastRef.current = true;
          toast.success('Course started');
        } else {
          courseStartedToastRef.current = true;
        }
      }

      if (closeSec <= 60 && closeSec > 0 && !warnShownRef.current) {
        warnShownRef.current = true;
        toast('Call will end in less than 1 minute.');
      }
      if (closeSec <= 0 && !endingRef.current) {
        toast.error('Course time ended. Call closed.');
        endingRef.current = true;
        await cleanupTracks();
        try {
          await client?.leave();
        } catch (_) {}
        resetCallTiming();
        onSessionEnd?.();
        onAutoEndNavigate?.();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      clearInterval(interval);
      resetCallTiming();
    };
  }, [
    session?.joinCloseAt,
    session?.joinOpenAt,
    cleanupTracks,
    client,
    onSessionEnd,
    onAutoEndNavigate,
    resumeRemoteAudio,
    pauseLobbyRemoteAudio,
  ]);

  // beforeunload
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      try {
        client?.leave?.();
      } catch (_) {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [client]);

  const setMicOn = useCallback(async (next) => {
    if (micToggleLockRef.current) return;
    const value = typeof next === 'function' ? next(micOn) : next;
    const track = micTrackRef.current;
    if (!track) {
      setMicOnState(false);
      return;
    }
    micToggleLockRef.current = true;
    try {
      await track.setEnabled(Boolean(value));
      setMicOnState(Boolean(value));
    } catch (err) {
      toast.error('Could not toggle microphone.');
    } finally {
      micToggleLockRef.current = false;
    }
  }, [micOn]);

  const setCamOn = useCallback(async (next) => {
    if (camToggleLockRef.current) return;
    if (isScreenSharing) {
      toast('Stop screen sharing before toggling the camera.');
      return;
    }
    const value = typeof next === 'function' ? next(camOn) : next;
    const track = camTrackRef.current;
    if (!track) {
      setCamOnState(false);
      return;
    }
    const enabled = Boolean(value);
    if (enabled === camOn) return;

    camToggleLockRef.current = true;
    try {
      // Ensure published when turning back on (only if missing)
      if (enabled) {
        const pub = client?.localTracks || [];
        const isPub = pub.includes(track);
        if (!isPub) {
          try {
            await client.publish(track);
          } catch (_) {}
        }
      }
      await track.setEnabled(enabled);
      setCamOnState(enabled);
    } catch (err) {
      toast.error('Could not toggle camera.');
      // Leave previous camOn state unchanged on failure
    } finally {
      camToggleLockRef.current = false;
    }
  }, [camOn, client, isScreenSharing]);

  const stopScreenShare = useCallback(async () => {
    const screen = screenTrackRef.current;
    const screenAudio = screenAudioRef.current;
    if (!screen && !isScreenSharing) return;

    try {
      const toUnpub = [screen, screenAudio].filter(Boolean);
      if (toUnpub.length) {
        try {
          await client.unpublish(toUnpub);
        } catch (_) {}
      }
    } finally {
      closeTrack(screen);
      closeTrack(screenAudio);
      screenTrackRef.current = null;
      screenAudioRef.current = null;
    }

    const restoreCam = camOnBeforeShareRef.current;
    if (camTrackRef.current && restoreCam) {
      try {
        await camTrackRef.current.setEnabled(true);
        const pub = client?.localTracks || [];
        if (!pub.includes(camTrackRef.current)) {
          await client.publish(camTrackRef.current);
        }
        setLocalVideoTrack(camTrackRef.current);
        setCamOnState(true);
      } catch (_) {
        setCamOnState(false);
      }
    } else {
      setLocalVideoTrack(camTrackRef.current);
      setCamOnState(false);
    }
    setIsScreenSharing(false);
  }, [client, isScreenSharing]);

  const startScreenShare = useCallback(async () => {
    if (!screenShareSupported) {
      toast.error('Screen sharing is not supported in this browser.');
      return;
    }
    if (isScreenSharing) {
      await stopScreenShare();
      return;
    }

    let screenTrack = null;
    let screenAudio = null;
    try {
      const result = await AgoraRTC.createScreenVideoTrack(
        { encoderConfig: '1080p_1', optimizationMode: 'detail' },
        'auto',
      );
      if (Array.isArray(result)) {
        [screenTrack, screenAudio] = result;
      } else {
        screenTrack = result;
      }
    } catch (err) {
      if (isPermissionCancel(err)) return;
      toast.error('Could not start screen share.');
      return;
    }

    camOnBeforeShareRef.current = camOn;
    try {
      if (camTrackRef.current) {
        try {
          await client.unpublish(camTrackRef.current);
        } catch (_) {}
        try {
          await camTrackRef.current.setEnabled(false);
        } catch (_) {}
      }
      const toPub = [screenTrack, screenAudio].filter(Boolean);
      await client.publish(toPub);
      screenTrackRef.current = screenTrack;
      screenAudioRef.current = screenAudio;
      setLocalVideoTrack(screenTrack);
      setIsScreenSharing(true);
      setCamOnState(false);

      screenTrack.on('track-ended', () => {
        stopScreenShare();
      });
    } catch (err) {
      closeTrack(screenTrack);
      closeTrack(screenAudio);
      screenTrackRef.current = null;
      screenAudioRef.current = null;
      try {
        if (camTrackRef.current && camOnBeforeShareRef.current) {
          await camTrackRef.current.setEnabled(true);
          await client.publish(camTrackRef.current);
          setLocalVideoTrack(camTrackRef.current);
          setCamOnState(true);
        }
      } catch (_) {}
      toast.error('Could not start screen share.');
    }
  }, [screenShareSupported, isScreenSharing, stopScreenShare, camOn, client]);

  const enableBlockedAudio = useCallback(() => {
    const users = client?.remoteUsers || (remoteUser ? [remoteUser] : []);
    users.forEach((user) => {
      const track = user?.audioTrack;
      if (!track) return;
      const id = track.getTrackId?.() || `${user.uid}-audio`;
      playedAudioIdsRef.current.delete(id);
      track
        .play()
        .then(() => {
          playedAudioIdsRef.current.add(id);
          setAudioBlocked(false);
        })
        .catch(() => {
          setAudioBlocked(true);
        });
    });
  }, [client, remoteUser]);

  const retryJoin = useCallback(async () => {
    setError(null);
    reconnectAttemptRef.current = 0;
    lastRenewAtRef.current = 0;
    const ok = await renewOrRejoin({ rejoin: true, trigger: 'retry' });
    if (!ok) setError('Unable to rejoin. Tap Retry or leave the call.');
  }, [renewOrRejoin]);

  const teardown = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    clearHardDisconnectTimer();
    if (hardReconnectBackoffRef.current) {
      clearTimeout(hardReconnectBackoffRef.current);
      hardReconnectBackoffRef.current = null;
    }
    await cleanupTracks();
    try {
      await client?.leave();
    } catch (_) {}
    joinedRef.current = false;
    hasJoinedOnceRef.current = false;
    clearPlayedAudioCache();
    lobbyAudioPausedRef.current = false;
    resetCallStats();
    resetCallTiming();
    onSessionEnd?.();
  }, [cleanupTracks, client, onSessionEnd, clearHardDisconnectTimer, clearPlayedAudioCache]);

  useEffect(() => {
    if (teardownRef) teardownRef.current = teardown;
    return () => {
      if (teardownRef) teardownRef.current = null;
    };
  }, [teardown, teardownRef]);

  // Coarse connection for context — avoid RECONNECTING↔CONNECTED flapping thrashing UI trees
  const hasJoinedOnce = hasJoinedOnceRef.current;
  const initialJoining = joining && !hasJoinedOnce;
  const connectionBanner =
    connectionState === 'RECONNECTING' || (joining && hasJoinedOnce)
      ? 'RECONNECTING'
      : connectionState;
  const effectiveRemoteUser = remoteUser || hookUsers[0] || null;
  const effectiveRemotePresent = remotePresent || hookUsers.length > 0;

  const mediaValue = useMemo(
    () => ({
      micOn,
      camOn,
      isScreenSharing,
      connectionState: connectionBanner,
      error,
      audioBlocked,
      micAvailable,
      camAvailable,
      screenShareSupported,
      localMicrophoneTrack: localAudioTrack,
      localCameraTrack: isScreenSharing ? null : localVideoTrack,
      localScreenTrack: isScreenSharing ? localVideoTrack : null,
      localVideoTrack,
      remoteUser: effectiveRemoteUser,
      remotePresent: effectiveRemotePresent,
      remoteTick,
      joining,
      initialJoining,
      qualityLabel: QUALITY_LABELS,
      setMicOn,
      setCamOn,
      startScreenShare,
      stopScreenShare,
      enableBlockedAudio,
      retryJoin,
      teardown,
    }),
    [
      micOn,
      camOn,
      isScreenSharing,
      connectionBanner,
      error,
      audioBlocked,
      micAvailable,
      camAvailable,
      screenShareSupported,
      localAudioTrack,
      localVideoTrack,
      effectiveRemoteUser,
      effectiveRemotePresent,
      remoteTick,
      joining,
      initialJoining,
      setMicOn,
      setCamOn,
      startScreenShare,
      stopScreenShare,
      enableBlockedAudio,
      retryJoin,
      teardown,
    ],
  );

  return (
    <CallMediaContext.Provider value={mediaValue}>
      {children}
    </CallMediaContext.Provider>
  );
};
